import { CssBaseline } from '@mui/material'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom'
import { setActiveEventSlug } from './services/eventService'
import { useAttendeeAuth } from './contexts/AttendeeAuthContext'
import { ThemeContextProvider } from './contexts/ThemeContext'
import { AppDataProvider } from './contexts/AppDataContext'
import { AttendeeAuthProvider } from './contexts/AttendeeAuthContext'
import { AuthProvider, useAuth } from './auth/AuthProvider'
import RequireAttendee from './auth/RequireAttendee'
import RequireProfile from './auth/RequireProfile'
import { LoadingState } from './components/ui/States'
import EventThemeProvider from './theme/EventThemeProvider'
import PwaManager from './pwa/PwaManager'
import AppShell from './components/AppShell'
import ErrorBoundary from './components/ErrorBoundary'
import LandingPage from './pages/LandingPage'
import VerifyPage from './pages/VerifyPage'
import PublicRegisterPage from './pages/PublicRegisterPage'
import EventSignInPage from './pages/EventSignInPage'
import PageView from './pages/PageView'
import AgendaPage from './pages/AgendaPage'
import SessionDetailPage from './pages/SessionDetailPage'
import SpeakersPage from './pages/SpeakersPage'
import SpeakerDetailPage from './pages/SpeakerDetailPage'
import ContentPage from './pages/ContentPage'
import FormsPage from './pages/FormsPage'
import FormDetailPage from './pages/FormDetailPage'
import RegisterPage from './pages/RegisterPage'
import BadgePage from './pages/BadgePage'
import WatchPage from './pages/WatchPage'
import NetworkPage from './pages/NetworkPage'
import EventInfoPage from './pages/EventInfoPage'
import ProfilePage from './pages/ProfilePage'
import NotFoundPage from './pages/NotFoundPage'
import AdminApp from './admin/AdminApp'

/** The attendee app shell + pages (gated by attendee auth). */
function AppRoutes() {
  return (
    <RequireAttendee>
      <RequireProfile>
        <EventThemeProvider>
        <AppShell>
          <Routes>
            <Route index element={<PageView slug="home" />} />
            <Route path="p/:slug" element={<PageView />} />
            <Route path="agenda" element={<AgendaPage />} />
            <Route path="agenda/:id" element={<SessionDetailPage />} />
            <Route path="speakers" element={<SpeakersPage />} />
            <Route path="speakers/:id" element={<SpeakerDetailPage />} />
            <Route path="content" element={<ContentPage />} />
            <Route path="forms" element={<FormsPage />} />
            <Route path="forms/:id" element={<FormDetailPage />} />
            <Route path="my-registration" element={<RegisterPage />} />
            <Route path="badge" element={<BadgePage />} />
            <Route path="watch" element={<WatchPage />} />
            <Route path="network" element={<NetworkPage />} />
            <Route path="info" element={<EventInfoPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AppShell>
        </EventThemeProvider>
      </RequireProfile>
    </RequireAttendee>
  )
}

/**
 * Event-scoped attendee app. Reads :slug from the route, points the data layer
 * at that event, and remounts the data provider (key=slug) so switching events
 * fully reloads. Legacy /app (no slug) falls back to the "current" event.
 *
 * The slug is applied synchronously during render (not in an effect) so the
 * data layer is pointed at the right event before AppDataProvider's load runs.
 */
function ScopedApp({ slug = null }: { slug?: string | null }) {
  const { ready, authAvailable, isAuthenticated, me } = useAttendeeAuth()
  const { pathname } = useLocation()

  // Legacy /app mount: if a signed-in attendee has a known event, send them to
  // their scoped URL (preserving the sub-path) so returning visitors land on
  // their own event rather than the generic "current" one.
  if (slug === null && isAuthenticated && me?.eventSlug) {
    const sub = pathname.replace(/^\/app/, '')
    return <Navigate to={`/e/${me.eventSlug}${sub}`} replace />
  }

  // Unauthenticated visit to a specific event → the event's OWN branded sign-in
  // page (not the generic "/" directory), so per-event links stay on-brand.
  if (ready && authAvailable && !isAuthenticated && slug) {
    return <Navigate to={`/e/${slug}/signin`} replace />
  }

  setActiveEventSlug(slug)
  return (
    <AppDataProvider key={slug ?? '__current__'}>
      <AppRoutes />
    </AppDataProvider>
  )
}

function ScopedAppRoute() {
  const { slug } = useParams()
  return <ScopedApp slug={slug ?? null} />
}

/**
 * While an MSAL redirect return is being processed, show a loading screen
 * instead of the routed page. MSAL is about to navigate back to the original
 * request URL (e.g. /admin); rendering routes here would briefly flash the
 * landing page at "/".
 */
function RoutedApp() {
  const { authRedirectInProgress } = useAuth()
  if (authRedirectInProgress) {
    return <LoadingState label="Signing you in…" />
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/e/:slug/verify" element={<VerifyPage />} />
      <Route path="/e/:slug/register" element={<PublicRegisterPage />} />
      <Route path="/e/:slug/signin" element={<EventSignInPage />} />
      <Route path="/e/:slug/*" element={<ScopedAppRoute />} />
      <Route path="/app/*" element={<ScopedApp slug={null} />} />
      <Route path="/admin/*" element={<AdminApp />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <ThemeContextProvider>
      <CssBaseline />
      <AuthProvider>
        <AttendeeAuthProvider>
          <ErrorBoundary label="app">
            <RoutedApp />
          </ErrorBoundary>
        </AttendeeAuthProvider>
        {/* PWA lifecycle UI is web-only; the native app has no service worker. */}
        {!import.meta.env.VITE_MOBILE && <PwaManager />}
      </AuthProvider>
    </ThemeContextProvider>
  )
}

export default App
